import { IonIcon, useIonRouter } from "@ionic/react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { documentsOutline } from "ionicons/icons";
import { InfoModal } from "@/components/molecules/info-modal";
import { DeclarationPreviewModal } from "@/components/molecules/shift/declaration-preview-modal";
import { EntryStatusChip } from "@/components/molecules/shift/entry-status-chip";
import { HoursFormModal } from "@/components/molecules/shift/hours-form-modal";
import { HoursPreviewModal } from "@/components/molecules/shift/hours-preview-modal";
import { TravelExpensesFormModal } from "@/components/molecules/shift/travel-expenses-form-modal";
import { type Shift } from "@/lib/backend/api";
import {
  useDeclarationInfoSections,
  useHoursInfoSections,
  useTravelExpensesInfoSections,
} from "@/lib/constants/application-info";
import { useCompletedShiftActions } from "@/lib/hooks/use-completed-shift-actions";
import { useShiftDisplayTimeDetailed } from "@/lib/hooks/use-shift-display-time";
import { timeIcon } from "@/lib/icons/time.icon";
import { Routes } from "@/lib/router";
import { opensHoursPreview } from "@/lib/shift/employee-hours-response";
import { isSettled, needsAttention, toEntryState } from "@/lib/shift/entry-status";
import { formatInTz } from "@/utils/format-datetime";

interface CompletedShiftCardOverviewProps {
  shift: Shift;
}

export function CompletedShiftCardRework({ shift }: CompletedShiftCardOverviewProps) {
  const { t } = useTranslation();
  const declarationInfoSections = useDeclarationInfoSections();
  const hoursInfoSections = useHoursInfoSections();
  const travelExpensesInfoSections = useTravelExpensesInfoSections();
  const router = useIonRouter();
  const infoModalRef = useRef<HTMLIonModalElement>(null);
  const hoursInfoModalRef = useRef<HTMLIonModalElement>(null);
  const hoursPreviewModalRef = useRef<HTMLIonModalElement>(null);
  const travelExpensesInfoModalRef = useRef<HTMLIonModalElement>(null);

  // Calculate display time - shows actual worked hours if available
  const displayTime = useShiftDisplayTimeDetailed(shift);

  // All business logic is handled by the state machine hook
  const {
    permissions,
    hourButtonState,
    declarationButtonState,
    expenseClaim,
    expenseClaimFileUrl,
    isLoadingExpenseClaim,
    isErrorExpenseClaim,
    formHours,
    FormElementHours,
    employeeResponseMutation,
    formTravelExpenses,
    FormElementExpenses,
    validator,
    ClockInWebScanner,
    ClockOutWebScanner,
    openHoursModal,
    openExpensesModal,
    openDeclarationPreview,
    closeModal,
    modalHoursRef,
    modalExpensesRef,
    modalDeclarationPreviewRef,
  } = useCompletedShiftActions(shift);

  // Parse dates for display — in projecttijd. `getDate()` en `toLocaleDateString()` lazen allebei
  // de klok van het toestel: op een telefoon westelijk van Greenwich stond er de dag ervóór boven
  // een dienst.
  const timeZone = shift.timeZone ?? shift.project?.timeZone;
  const day = formatInTz(shift.startAt, { day: "numeric", timeZone });
  const month = formatInTz(shift.startAt, { month: "short", timeZone });

  // Opens the shift, not the project it happened to belong to — the same destination an upcoming
  // shift has had since the shift screen existed. That screen now has a finished form: the hours
  // and the claim in place of offering it away or asking to be taken off it.
  const handleCardClick = () => {
    router.push(Routes.ShiftDetail({ id: shift.id }), "forward");
  };

  const handleHoursClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (opensHoursPreview(shift.shiftTimeEntry, displayTime.isLocked)) {
      // Read-only zodra de uren vastliggen, en ook zodra het kantoor ze invoerde: dan corrigeert
      // de medewerker ze niet maar reageert hij erop, en die reactie zit hier. Eigen invoer blijft
      // tot de goedkeuring bewerkbaar; ingediend hebben is geen reden om er niet meer bij te
      // mogen (#495).
      hoursPreviewModalRef.current?.present();
    } else if (hourButtonState.action) {
      // QR code action
      hourButtonState.action();
    } else {
      // Manual entry modal
      openHoursModal();
    }
  };

  const handleDeclarationClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (expenseClaim) {
      // Show preview if expense claim exists
      openDeclarationPreview();
    } else {
      // ANDROID FIX: Reset form BEFORE opening modal to clear stale data
      // This ensures fresh state on each submission attempt
      // Fixes issue where first upload fails but second succeeds
      formTravelExpenses.reset();
      // Show form if no expense claim
      openExpensesModal();
    }
  };

  // Two tracks, moving independently: the hours and the expense claim. Both were previously
  // conveyed by colour alone — three near-identical switch statements returning tailwind classes,
  // and for the declaration nothing but the tint of the word "Declaratie". Colour is now one of
  // three signals the chip carries, next to a word and an icon.
  const hoursState = toEntryState(shift.shiftTimeEntry?.status);
  const expensesState = toEntryState(expenseClaim?.status);

  // Rejected hours put the ball back with the employee, so they get the primary button — the same
  // one that offers to fill them in. Previously the button disappeared the moment the fields were
  // complete, which is exactly when a rejection happens, leaving a grey underlined link as the
  // only way back in.
  //
  // Settled hours never do, whatever the fields say. An approved or paid entry with no break time
  // read as incomplete and got a button, so the app invited people to submit hours that were
  // already on their payslip.
  const hoursNeedYou =
    !isSettled(hoursState) && (!displayTime.isFullyDeclared || needsAttention(hoursState));

  return (
    <>
      {/* A finished shift is a row, not a poster. It used to render a 3:2 project image, the
          name, and two equally-weighted full-width buttons — the layout of an upcoming shift,
          pasted onto something you can only look back at. Visual weight follows attention:
          what still needs doing gets a card, what happened gets a line. */}
      <div
        onClick={handleCardClick}
        className="flex w-full cursor-pointer items-start gap-3 border-b border-solid border-bordercolor-primary pb-3"
      >
        {/* Date indicator */}
        <div className="flex w-9 shrink-0 flex-col items-center capitalize">
          <p className="text-17 font-bold leading-[1.1] text-txt-primary">{day}</p>
          <p className="text-xs font-medium uppercase leading-[1.3] text-txt-tertiary">{month}</p>
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <h3 className="min-w-0 flex-1 truncate text-15 font-semibold leading-[1.3] text-txt-primary">
            {shift.project?.name}
          </h3>

          <p className="text-13 leading-[1.4] text-txt-secondary">
            {displayTime.startTime} - {displayTime.endTime}
            {displayTime.isFullyDeclared && displayTime.durationDutch && (
              <span className="text-txt-tertiary"> · {displayTime.durationDutch}</span>
            )}
          </p>

          {/* Where things stand, one chip per track. A chip only exists once that track does:
              nothing submitted means a button below, not an empty status. */}
          {(hoursState || expensesState) && (
            <div className="flex flex-wrap gap-1.5">
              {hoursState && (
                <EntryStatusChip
                  kind="hours"
                  status={shift.shiftTimeEntry?.status}
                  onClick={handleHoursClick}
                />
              )}
              {expensesState && (
                <EntryStatusChip
                  kind="expenses"
                  status={expenseClaim?.status}
                  onClick={handleDeclarationClick}
                />
              )}
            </div>
          )}

          {/* Buttons only where there is genuinely something to do. They used to sit on every
              completed shift, including the ones that were long since settled. */}
          {/* Zonder invoerrecht is de regel er om te lezen en op te reageren (admin#1093). De chip
              hierboven brengt je naar de preview; een actieknop hoort er dan niet bij. */}
          {((hourButtonState.isVisible && hoursNeedYou && !hourButtonState.isReadOnly) ||
            (declarationButtonState.isVisible && !expenseClaim)) && (
            <div className="flex flex-wrap gap-2 pt-1">
              {hourButtonState.isVisible && hoursNeedYou && !hourButtonState.isReadOnly && (
                <button
                  type="button"
                  onClick={handleHoursClick}
                  disabled={hourButtonState.isDisabled}
                  className="flex items-center gap-1.5 rounded-lg border-2 border-solid border-button-primary bg-button-primary px-3 py-1.5 disabled:opacity-50"
                >
                  <span className="text-13 font-semibold leading-[1.4] text-txt-inverted">
                    {/* QR projects: show the live clock action ("Uitklokken"/"Inklokken") so an
                        employee clocking out after the scheduled end time recognises the button.
                        After a rejection the label says what is actually being asked — correcting
                        what you already submitted, not entering it for the first time. */}
                    {permissions.enableQRCodeClocking
                      ? hourButtonState.text
                      : needsAttention(hoursState)
                        ? t("shift:hours.edit")
                        : t("shift:hours.title")}
                  </span>
                  <IonIcon icon={timeIcon} className="size-3.5 text-txt-inverted" />
                </button>
              )}

              {declarationButtonState.isVisible && !expenseClaim && (
                <button
                  type="button"
                  onClick={handleDeclarationClick}
                  disabled={declarationButtonState.isDisabled}
                  className="flex items-center gap-1.5 rounded-lg border border-solid border-bordercolor-primary px-3 py-1.5 disabled:opacity-50"
                >
                  <span className="text-13 font-semibold leading-[1.4] text-txt-primary">
                    {t("shift:declaration.title")}
                  </span>
                  <IonIcon icon={documentsOutline} className="size-3.5 text-txt-primary" />
                </button>
              )}
            </div>
          )}

          {/* Settled hours and claims stay reachable through their own chip — no second row of
              grey underlined links saying the same thing one line lower. */}
        </div>
      </div>
      {/* Hours Modal - Only for manual entry (not QR) */}
      <HoursFormModal
        modalRef={modalHoursRef}
        hoursInfoModalRef={hoursInfoModalRef}
        formHours={formHours}
        FormElementHours={FormElementHours}
        validator={validator}
        closeModal={closeModal}
      />
      {/* Travel Expenses Modal */}
      <TravelExpensesFormModal
        modalRef={modalExpensesRef}
        travelExpensesInfoModalRef={travelExpensesInfoModalRef}
        formTravelExpenses={formTravelExpenses}
        FormElementExpenses={FormElementExpenses}
        closeModal={() => {
          formTravelExpenses.reset();
          closeModal();
        }}
      />
      {/* Declaration Preview Modal */}
      <DeclarationPreviewModal
        modalRef={modalDeclarationPreviewRef}
        infoModalRef={infoModalRef}
        expenseClaim={expenseClaim}
        expenseClaimFileUrl={expenseClaimFileUrl}
        isLoadingExpenseClaim={isLoadingExpenseClaim}
        isErrorExpenseClaim={isErrorExpenseClaim}
        closeModal={closeModal}
      />
      {/* Hours Preview Modal — klopt / klopt niet (admin#624) wired the same way as on the
          legacy row this replaces. */}
      <HoursPreviewModal
        modalRef={hoursPreviewModalRef}
        hoursInfoModalRef={hoursInfoModalRef}
        shift={shift}
        durationDutch={displayTime.durationDutch}
        onRespond={(response, note) => employeeResponseMutation.mutate({ response, note })}
        isRespondingToHours={employeeResponseMutation.isPending}
      />
      {/* Declaration Info Modal */}
      <InfoModal
        modalRef={infoModalRef}
        title={t("shift:declaration.info.title")}
        sections={declarationInfoSections}
      />
      {/* Hours Info Modal */}
      <InfoModal
        modalRef={hoursInfoModalRef}
        title={t("shift:hours.info.title")}
        sections={hoursInfoSections}
      />
      {/* Travel Expenses Info Modal */}
      <InfoModal
        modalRef={travelExpensesInfoModalRef}
        title="Informatie over reiskosten"
        sections={travelExpensesInfoSections}
      />
      {/* QR Scanners */}
      {ClockInWebScanner && <ClockInWebScanner />}
      {ClockOutWebScanner && <ClockOutWebScanner />}
    </>
  );
}
